import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api/client";
import { ComboSelect } from "../../components/ComboSelect";
import { AiCombo } from "../../components/AiCombo";
import { useNotify } from "../../components/notifications";
import { useSaveFlow } from "../../components/confirm";
import { useHotkeys, formatCombo } from "../../components/hotkeys";
import { QuickAddDialog } from "./QuickAddDialog";
import type { CrudField } from "./moduleCrud";
import "./SettingsDetailPage.css"; // reuse the Settings-detail `.sd-*` chrome

const WATER_SYSTEM_OPTIONS_URL = "/api/v1/msr-water-system/options";

/** The legacy "New" button beside System: add a Water System inline, through the
 *  Water System master's own (audited) endpoint, then select it. */
const WATER_SYSTEM_QUICK_ADD: NonNullable<CrudField["quickAdd"]> = {
  createUrl: "/api/v1/msr-water-system",
  title: "MSR Water Systems",
  nameKey: "systemName",
  // G5 MSR Water Systems popup (owner request 2026-10-15): System ID shown read-only ("Autogenerate" —
  // the server mints it), System Name, a multi-line Description, and the footer OK · Cancel.
  okFirst: true,
  fields: [
    { key: "systemId", label: "System ID", type: "readonly", defaultValue: "Autogenerate" },
    { key: "systemName", label: "System Name", type: "text", required: true, maxLength: 200 },
    { key: "description", label: "Description", type: "textarea" },
  ],
};

/**
 * Settings → Reporting → MSR → MSR Data Import Config — FULL-PAGE detail
 * (ALPACA-LIMS `MSRDataImportConfiguration` parity). One config row per water
 * system tells the Monthly Summary Report how to pull a month's samples.
 *
 * This REPLACES the old popup (`MsrDataImportConfigDialog`, removed 2026-09-26)
 * so New/Edit opens the shared full-page Detail View like every other Settings
 * form (standing rule 12 — NOT a popup). It stays a BESPOKE full-page component
 * (dispatched by ModuleListView, same pattern as `SampleFieldConfigDetail`)
 * rather than the generic `SettingsDetailPage`, because its five id-lists
 * (Sample Type(s) / Client / Project / PWS ID / Sample Site) are type-to-search
 * multi-select GUID pickers — the shared detail's multiselect loads every option
 * up front, which these large lists can't afford. The header uses the sideLabels
 * standard (label BESIDE its field, single column). Options come from
 * `GET /api/v1/msr-data-import-config/options/{kind}` (q + ids); the System
 * dropdown from `GET /api/v1/msr-water-system/options`.
 *
 * `MSRCategory` is server-derived from the chosen System (= its SystemName), so
 * it's shown read-only. `ImportMode` is the mutually-exclusive Stored Procedure
 * / Excel Import choice. Save runs through the shared `useSaveFlow`
 * ("Do you want to Save?" → POST/PUT → "Saved successfully." → back to list;
 * Edit gates the audit reason, Create is exempt).
 */
interface Props {
  /** null = New; an Oid = Edit (form prefilled from GET {id}). */
  id: string | null;
  /** How to open an existing record: "view" (read-only + Edit button, the
   *  row-click default) or "edit" (straight to the Save mode, the Edit pencil).
   *  A New record (id null) always opens in edit. */
  initialMode?: "view" | "edit";
  title?: string;
  group?: string;
  onHome?: () => void;
  onSaved: () => void;
  onClose: () => void;
}

interface MsrOption {
  id: string;
  name: string;
}

/** The five multi-select lookups → their backend `options/{kind}` route. */
const LOOKUPS: { field: LookupField; kind: string; label: string }[] = [
  { field: "sampleTypes", kind: "sample-types", label: "Sample Type" },
  { field: "client", kind: "clients", label: "Client" },
  { field: "project", kind: "projects", label: "Project" },
  { field: "pwsId", kind: "pws-ids", label: "PWS ID" },
  { field: "sampleSite", kind: "sample-sites", label: "Sample Site" },
];
type LookupField = "sampleTypes" | "client" | "project" | "pwsId" | "sampleSite";

/** Rows in the LEFT column of the column-major grid: MSR Category + System + the pickers + SP/Excel. */
const LEFT_COLUMN_ROWS = 3 + LOOKUPS.length;

// The five ';'-joined GUID-list fields, kept lowercase (the options endpoint
// serialises Guids lowercase; GUIDs are case-insensitive in SQL so the saved
// value round-trips either way — lowercasing just keeps the checkbox state and
// the <select> value matching).
type Lists = Record<LookupField, string>;
const EMPTY_LISTS: Lists = { sampleTypes: "", client: "", project: "", pwsId: "", sampleSite: "" };

export function MsrDataImportConfigDetail({
  id,
  initialMode,
  title,
  group,
  onHome,
  onSaved,
  onClose,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();

  const [curId, setCurId] = useState<string | null>(id);
  const editing = curId != null;
  // View/edit flow (standing rule 12): a row-click opens an existing record
  // read-only with an Edit button; the Edit pencil / New open straight to Save
  // mode. New (id null) is always editable.
  const [mode, setMode] = useState<"view" | "edit">(id == null ? "edit" : (initialMode ?? "view"));
  const viewOnly = mode === "view";

  const [systems, setSystems] = useState<MsrOption[]>([]);
  const [addingSystem, setAddingSystem] = useState(false);
  const [system, setSystem] = useState(""); // MSRWaterSystem Oid (lowercase)
  const [msrCategory, setMsrCategory] = useState(""); // prefilled category (edit); preview = system name
  // Module + BO — optional single-selects, both FK → dbo.DefaultSetting (shown by
  // ModuleName), re-added 2026-09-26 to match the legacy form.
  const [moduleBoOptions, setModuleBoOptions] = useState<MsrOption[]>([]);
  const [module, setModule] = useState(""); // DefaultSetting Oid (lowercase)
  const [bo, setBo] = useState(""); // DefaultSetting Oid (lowercase)
  const [lists, setLists] = useState<Lists>(EMPTY_LISTS);
  const [importMode, setImportMode] = useState<"sp" | "excel">("sp");
  // NullProject is preserved but no longer edited on the form (control removed
  // 2026-09-26); its stored value round-trips untouched through save.
  const [nullProject, setNullProject] = useState(false);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(editing);
  const [reloadTick, setReloadTick] = useState(0);

  const resetForm = () => {
    setSystem("");
    setMsrCategory("");
    setModule("");
    setBo("");
    setLists(EMPTY_LISTS);
    setImportMode("sp");
    setNullProject(false);
    setComment("");
  };

  // System dropdown options (small reference list — load once).
  useEffect(() => {
    let alive = true;
    api
      .get<{ data?: MsrOption[] }>(WATER_SYSTEM_OPTIONS_URL, { silentLoading: true } as never)
      .then((r) => {
        if (alive) setSystems(r.data.data ?? []);
      })
      .catch(() => {
        if (alive) notify.error("Could not load Water Systems.");
      });
    return () => {
      alive = false;
    };
  }, [notify]);

  // Module / BO dropdown options (all live DefaultSetting rows — load once).
  useEffect(() => {
    let alive = true;
    api
      .get<{ data?: MsrOption[] }>("/api/v1/msr-data-import-config/module-bo-options", { silentLoading: true } as never)
      .then((r) => {
        if (alive) setModuleBoOptions(r.data.data ?? []);
      })
      .catch(() => {
        /* optional lookups — leave empty on failure */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Edit / reload: prefill the whole form from the config detail.
  useEffect(() => {
    if (!curId) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .get<{ data?: Record<string, unknown> }>(`/api/v1/msr-data-import-config/${curId}`)
      .then((r) => {
        if (!alive) return;
        const d = r.data.data ?? {};
        setSystem(String(d.system ?? "").toLowerCase());
        setMsrCategory(String(d.msrCategory ?? ""));
        setModule(String(d.module ?? "").toLowerCase());
        setBo(String(d.bo ?? "").toLowerCase());
        setLists({
          sampleTypes: String(d.sampleTypes ?? "").toLowerCase(),
          client: String(d.client ?? "").toLowerCase(),
          project: String(d.project ?? "").toLowerCase(),
          pwsId: String(d.pwsId ?? "").toLowerCase(),
          sampleSite: String(d.sampleSite ?? "").toLowerCase(),
        });
        setImportMode(String(d.importMode ?? "sp") === "excel" ? "excel" : "sp");
        setNullProject(Boolean(d.nullProject));
        setComment(String(d.comment ?? ""));
      })
      .catch(() => {
        if (alive) notify.error("Could not load this MSR Data Import Config.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [curId, reloadTick, notify]);

  // MSR Category preview: the chosen System's name (= what the server stores);
  // falls back to the prefilled value until the System list resolves.
  const categoryPreview = (system && systems.find((s) => s.id === system)?.name) || msrCategory || "";

  const setList = (field: LookupField, value: string) => setLists((prev) => ({ ...prev, [field]: value }));

  // Save via the ONE shared flow (§18.6). Server derives MSRCategory + the
  // SP/Excel bits, mints nothing (no sequential ID), audits Update per field.
  const doSave = async (after: "stay" | "close" | "new") => {
    if (!system) {
      notify.warning("Please select a Water System.");
      return;
    }
    let newId = curId;
    await runSave({
      formName: "MSR Data Import Config",
      auditAction: editing ? "edit" : "create",
      save: async () => {
        const body = {
          system,
          module: module || null,
          bo: bo || null,
          sampleTypes: lists.sampleTypes || null,
          client: lists.client || null,
          project: lists.project || null,
          pwsId: lists.pwsId || null,
          sampleSite: lists.sampleSite || null,
          importMode,
          nullProject,
          comment: comment.trim() || null,
        };
        if (curId) await api.put(`/api/v1/msr-data-import-config/${curId}`, body);
        else {
          const r = await api.post<{ data?: { id?: string } }>("/api/v1/msr-data-import-config", body);
          newId = r.data.data?.id ?? null;
        }
      },
      onSaved: () => {
        if (after === "close") {
          onSaved();
          return;
        }
        if (after === "new") {
          resetForm();
          setCurId(null);
          return;
        }
        // Stay on the record — a new record flips into edit mode, an existing one reloads.
        if (newId && newId !== curId) setCurId(newId);
        else setReloadTick((n) => n + 1);
      },
    });
  };

  const recordName = editing ? `MSR Data Import Config — ${categoryPreview || "…"}` : "New MSR Data Import Config";

  // Keyboard shortcuts for this hand-rolled detail header (standing rule 14).
  // VIEW mode mirrors Edit / Close; EDIT mode mirrors Save / Save & New /
  // Save & Close / Cancel.
  useHotkeys(
    viewOnly
      ? [
          { combo: "alt+e", label: "Edit", group: "MSR Data Import Config", order: 1, run: () => setMode("edit") },
          { combo: "f2", label: "Edit", group: "MSR Data Import Config", order: 1, run: () => setMode("edit") },
          {
            combo: "esc",
            label: "Close",
            group: "MSR Data Import Config",
            order: 2,
            allowInInput: false,
            run: () => onClose(),
          },
        ]
      : [
          { combo: "mod+s", label: "Save", group: "MSR Data Import Config", order: 1, run: () => void doSave("stay") },
          {
            combo: "mod+shift+s",
            label: "Save & New",
            group: "MSR Data Import Config",
            order: 2,
            run: () => void doSave("new"),
          },
          {
            combo: "mod+enter",
            label: "Save & Close",
            group: "MSR Data Import Config",
            order: 3,
            run: () => void doSave("close"),
          },
          {
            combo: "esc",
            label: "Cancel",
            group: "MSR Data Import Config",
            order: 4,
            allowInInput: false,
            run: () => onClose(),
          },
        ],
  );

  // The single-select lists in the shape the shared <ComboSelect> takes (its popup is
  // capped and scrolls — a native <select> list cannot be height-limited).
  const systemOptions = useMemo(() => systems.map((s) => ({ value: s.id, label: s.name })), [systems]);
  const moduleBoComboOptions = useMemo(
    () => moduleBoOptions.map((o) => ({ value: o.id, label: o.name })),
    [moduleBoOptions],
  );

  // The SAME buttons, glyphs, wording and order as the shared SettingsDetailPage
  // ActionBar (Save · Save and Close · Save and New · | · Cancel; view = Edit · Close).
  const actionButtons = viewOnly ? (
    <>
      <button
        className="sd-abtn primary"
        onClick={() => setMode("edit")}
        disabled={loading}
        title={`Edit (${formatCombo("alt+e")})`}
      >
        <span className="tb-i tb-i-blue">✎</span> Edit
      </button>
      <button className="sd-abtn cancel" onClick={onClose} title={`Close (${formatCombo("esc")})`}>
        <span className="tb-i">↩︎</span> Close
      </button>
    </>
  ) : (
    <>
      <button
        className="sd-abtn primary"
        onClick={() => void doSave("stay")}
        disabled={loading}
        title={`Save (${formatCombo("mod+s")})`}
      >
        <span className="tb-i">💾</span> Save
      </button>
      <button
        className="sd-abtn"
        onClick={() => void doSave("close")}
        disabled={loading}
        title={`Save and Close (${formatCombo("mod+enter")})`}
      >
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button
        className="sd-abtn"
        onClick={() => void doSave("new")}
        disabled={loading}
        title={`Save and New (${formatCombo("mod+shift+s")})`}
      >
        <span className="tb-i">📄</span> Save and New
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={onClose} title={`Cancel (${formatCombo("esc")})`}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </>
  );

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div
            className="sd-head"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              {/* No load-time / Audit chips on the detail (owner request 2026-09-28)
                  — matches the standard SettingsDetailPage detail, which shows neither.
                  The chips remain on the LIST view. */}
              <div className="sd-bc">
                {onHome && (
                  <>
                    <a onClick={onHome}>Home</a>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                {group && (
                  <>
                    <span>{group}</span>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                {title && (
                  <>
                    <a onClick={onClose}>{title}</a>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                <span>{recordName}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>{actionButtons}</div>
          </div>

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading">Loading…</div>
            ) : (
              <>
                {/* The standard detail layout (reference form: Items) — 50/50 column-major
                    with side labels, square 24px control boxes (sd-grid--square). LEFT: MSR Category · System · the five pickers · SP/Excel;
                    RIGHT: Module · BO · Comment (the legacy grouping). */}
                <div
                  className="sd-grid sd-grid--colmajor sd-grid--side sd-grid--square"
                  style={{ "--sd-rows": String(LEFT_COLUMN_ROWS) } as CSSProperties}
                >
                  {/* MSR Category — server-derived (= the System's name), read-only.
                      Shown ABOVE System (owner request 2026-09-26 — swapped). */}
                  <div className="sd-row">
                    <label className="sd-label">MSR Category</label>
                    <input
                      className="sd-input readonly"
                      value={categoryPreview}
                      readOnly
                      title="Set automatically from the chosen System"
                    />
                  </div>

                  {/* System (required) — drives the read-only MSR Category above.
                      Read-only (disabled) in view mode. */}
                  <div className="sd-row">
                    <label className="sd-label">
                      System<span className="req">*</span>
                    </label>
                    {/* The dropdown + "+" standard (owner request 2026-10-15): the shared AiCombo — a green "+"
                        inside the box, welded to the orange caret, as every other Settings form with a quick-add
                        (SettingsDetailPage quickAdd.welded). In view mode the "+" is hidden and the box is locked. */}
                    <AiCombo
                      value={system || null}
                      options={systemOptions}
                      onChange={(v) => setSystem(v ?? "")}
                      onNew={viewOnly ? undefined : () => setAddingSystem(true)}
                      newTitle="Add Water System"
                      placeholder="— None —"
                      naLabel="— None —"
                      disabled={viewOnly}
                      className="msr-sys-combo"
                      compact
                    />
                    {addingSystem && (
                      <QuickAddDialog
                        spec={WATER_SYSTEM_QUICK_ADD}
                        optionsUrl={WATER_SYSTEM_OPTIONS_URL}
                        onClose={() => setAddingSystem(false)}
                        onAdded={(opts, selectedId) => {
                          setSystems(opts);
                          if (selectedId) setSystem(selectedId);
                        }}
                      />
                    )}
                  </div>

                  {/* Five type-to-search multi-select GUID pickers — the rest of the LEFT column. */}
                  {LOOKUPS.map((l) => (
                    <div className="sd-row" key={l.field}>
                      <label className="sd-label">{l.label}</label>
                      <div className="sd-ms">
                        <MsrLookupMultiSelect
                          kind={l.kind}
                          label={l.label}
                          value={lists[l.field]}
                          readOnly={viewOnly}
                          onChange={(v) => setList(l.field, v)}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Import mode — the legacy SP / Excel checkbox pair under Sample Site,
                      with no caption of its own. Mutually exclusive: ticking one unticks
                      the other, and the ticked one cannot be cleared. */}
                  <div className="sd-row">
                    <label className="sd-label" aria-hidden="true" />
                    <div className="msr-dic-mode">
                      <label className={viewOnly ? "readonly" : undefined}>
                        <input
                          type="checkbox"
                          checked={importMode === "sp"}
                          disabled={viewOnly}
                          onChange={() => setImportMode("sp")}
                        />
                        SP
                      </label>
                      <label className={viewOnly ? "readonly" : undefined}>
                        <input
                          type="checkbox"
                          checked={importMode === "excel"}
                          disabled={viewOnly}
                          onChange={() => setImportMode("excel")}
                        />
                        Excel
                      </label>
                    </div>
                  </div>

                  {/* RIGHT column starts here (LEFT_COLUMN_ROWS items precede it).
                      Module + BO — optional, both FK → dbo.DefaultSetting (ModuleName). */}
                  <div className="sd-row">
                    <label className="sd-label">Module</label>
                    <ComboSelect
                      className="sd-combo"
                      value={module}
                      options={moduleBoComboOptions}
                      placeholder="— None —"
                      disabled={viewOnly}
                      onChange={setModule}
                    />
                  </div>

                  <div className="sd-row">
                    <label className="sd-label">BO</label>
                    <ComboSelect
                      className="sd-combo"
                      value={bo}
                      options={moduleBoComboOptions}
                      placeholder="— None —"
                      disabled={viewOnly}
                      onChange={setBo}
                    />
                  </div>

                  {/* Null Project control removed 2026-09-26 (owner request) — the
                      stored NullProject value is preserved untouched through save. */}

                  {/* Comment. */}
                  <div className="sd-row sd-row--span2">
                    <label className="sd-label">Comment</label>
                    <textarea
                      className={`sd-input sd-input--tall${viewOnly ? " readonly" : ""}`}
                      value={comment}
                      readOnly={viewOnly}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          {/* The same action set again at the BOTTOM — standing rule 12. */}
          <div className="sd-actionbar bottom">{actionButtons}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Type-to-search multi-select over one MSR lookup. Stores its selection as a
 * ';'-joined GUID string (legacy XAF CheckedListBoxEditor format). Options load
 * lazily from `/api/v1/msr-data-import-config/options/{kind}` (server returns the
 * first 500 matching `q`, UNIONed with any `ids` so already-selected values keep
 * a label even outside the search window). Renders ONLY the control (button +
 * portalled popup) so it drops into the side-label grid's field column; the
 * parent renders the `.sd-label`. Popup uses the shared `.qs-combo*` chrome.
 */
function MsrLookupMultiSelect({
  kind,
  label,
  value,
  onChange,
  readOnly = false,
}: {
  kind: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  const ids = useMemo(
    () =>
      value
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean),
    [value],
  );
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState<MsrOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // Selection snapshot at open-time — sent as `ids` so the option list unions in
  // the currently-selected values, WITHOUT refetching on every checkbox toggle.
  const openIdsRef = useRef<string[]>([]);
  // Mirror of `labels` for the by-ids resolver effect (avoids a deps loop).
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const mergeLabels = (data: MsrOption[]) =>
    setLabels((prev) => {
      const n = { ...prev };
      data.forEach((o) => {
        n[o.id] = o.name;
      });
      return n;
    });

  // Resolve display names for already-selected ids (edit prefill / external
  // change) — only fetches the ids we don't already have a label for.
  useEffect(() => {
    const missing = ids.filter((x) => !labelsRef.current[x]);
    if (missing.length === 0) return;
    let alive = true;
    api
      .get<{ data?: MsrOption[] }>(`/api/v1/msr-data-import-config/options/${kind}`, {
        params: { ids: missing.join(",") },
        silentLoading: true,
      } as never)
      .then((r) => {
        if (alive) mergeLabels(r.data.data ?? []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [value, kind, ids]);

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Load options when the panel is open (debounced type-to-search).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api
      .get<{ data?: MsrOption[] }>(`/api/v1/msr-data-import-config/options/${kind}`, {
        params: { q: q || undefined, ids: openIdsRef.current.join(",") || undefined },
        silentLoading: true,
      } as never)
      .then((r) => {
        if (!alive) return;
        const data = r.data.data ?? [];
        setOpts(data);
        mergeLabels(data);
      })
      .catch(() => {
        if (alive) setOpts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, q, kind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const openPanel = () => {
    if (readOnly) return; // view mode — no picker
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    openIdsRef.current = ids;
    setInput("");
    setQ("");
    setOpen((o) => !o);
  };

  const toggle = (o: MsrOption) => {
    const set = new Set(ids);
    if (set.has(o.id)) set.delete(o.id);
    else set.add(o.id);
    setLabels((prev) => ({ ...prev, [o.id]: o.name }));
    onChange([...set].join(";"));
  };

  const allSelected = opts.length > 0 && opts.every((o) => ids.includes(o.id));
  const selectAll = () => {
    mergeLabels(opts);
    onChange([...new Set([...ids, ...opts.map((o) => o.id)])].join(";"));
  };

  // Show the actual selected VALUES joined (legacy XAF checked-list parity, owner
  // request 2026-09-28) instead of "N selected". Until every selected id's label has
  // resolved from the server, fall back to the count so raw GUIDs never flash; the
  // field truncates a long list with an ellipsis (.qs-combo-val) and the full list is
  // on the hover title.
  const allResolved = ids.every((id) => labels[id] != null);
  const summary =
    ids.length === 0
      ? "— None —"
      : allResolved
        ? ids.map((id) => labels[id]).join("; ")
        : `${ids.length} selected`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`qs-combo${readOnly ? " readonly" : ""}`}
        onClick={openPanel}
        disabled={readOnly}
        style={{ width: "100%" }}
        aria-label={label}
        title={ids.length > 0 && allResolved ? summary : undefined}
      >
        <span className={ids.length ? "qs-combo-val" : "qs-combo-ph"}>{summary}</span>
        {ids.length > 0 && !readOnly && (
          <i
            className="ti ti-x qs-combo-clear"
            title="Clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
          />
        )}
        {/* The chevron stays beside the clear ✕, so a field that already holds a
            value still reads as a dropdown you can open to add more. */}
        {!readOnly && <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open &&
        createPortal(
          <>
            <div className="qs-combo-backdrop" onMouseDown={() => setOpen(false)} />
            <div
              className="qs-combo-pop"
              style={{ top: pos.top, left: pos.left, minWidth: Math.max(240, pos.width) }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                }
              }}
            >
              <div className="qs-combo-search">
                <i className="ti ti-search" />
                <input
                  autoFocus
                  value={input}
                  placeholder="Type to filter…"
                  onChange={(e) => setInput(e.target.value)}
                />
              </div>
              {!loading && opts.length > 0 && (
                <div className="qs-combo-tools">
                  <button type="button" className="qs-combo-ftr-btn" onClick={selectAll} disabled={allSelected}>
                    Select all
                  </button>
                  <button
                    type="button"
                    className="qs-combo-ftr-btn red"
                    onClick={() => onChange("")}
                    disabled={ids.length === 0}
                  >
                    Clear
                  </button>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="qs-combo-ftr-ok" onClick={() => setOpen(false)}>
                    OK
                  </button>
                </div>
              )}
              <div className="qs-combo-list">
                {loading && <div className="qs-combo-empty">Loading…</div>}
                {!loading && opts.length === 0 && <div className="qs-combo-empty">No values</div>}
                {opts.map((o) => (
                  <label key={o.id} className={`qs-combo-opt${ids.includes(o.id) ? " sel" : ""}`} title={o.name}>
                    <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o)} />
                    <span>{o.name}</span>
                  </label>
                ))}
                {!loading && opts.length >= 500 && <div className="qs-combo-empty">First 500 — type to refine</div>}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
